import { Controller } from "@hotwired/stimulus"

/**
 * Download a chart as a PNG.
 *
 * Attached to a button with:
 *   data-controller="chart-download"
 *   data-chart-download-target-value="<canvas_id>"
 *   data-action="click->chart-download#download"
 *
 * Two chart shapes are supported (LRPO-5151):
 *   1. Canvas charts — Chartkick (`area_chart`, `column_chart`, `bar_chart`)
 *      render a wrapper <div id="..."> with a child <canvas>; the hand-rolled
 *      matrix heatmaps bind the id directly to the <canvas>. Both download via
 *      canvas.toDataURL() + a synthetic <a download> click, no server round-trip.
 *   2. SVG charts — Google GeoChart cards (Leads by US State / Country, Lead
 *      Quality by Geography, Daily Spread) render an <svg> inside the target
 *      <div>; there is no <canvas> to read. We serialize the <svg> and
 *      rasterize it to PNG via an Image + offscreen canvas before triggering
 *      the same synthetic download.
 *
 * Every failure path surfaces accessible user feedback (a role="status"
 * message rendered next to the button) instead of the previous silent
 * console.warn, so a click that can't produce a PNG never looks like a no-op.
 */
export default class extends Controller {
  // `filename` defaults to "" rather than "chart.png" so that a button
  // without an explicit `data-chart-download-filename-value` falls through
  // to the canvas id (e.g. "la-leads-per-day.png") in `_resolvedFilename`.
  // The previous "chart.png" default short-circuited that fallback and
  // every download landed on disk as the same generic name.
  static values = {
    target:   { type: String, default: "" },
    filename: { type: String, default: "" }
  }

  disconnect() {
    // After a Turbo navigation swaps this button out: cancel the pending
    // feedback timer/node AND flag in-flight async work (the SVG Image load)
    // so its onload/onerror don't fire a surprise download or attach a status
    // node to the now-detached button. Mirrors geo_chart_controller's abort guard.
    this._aborted = true
    if (this._statusTimer) clearTimeout(this._statusTimer)
    if (this._statusNode?.isConnected) this._statusNode.remove()
    this._statusNode = null
  }

  download(event) {
    if (event) event.preventDefault()

    const canvasId = this.targetValue
    if (!canvasId) {
      this._announceFailure("No chart is available to download.")
      return
    }

    // Canvas charts (Chartkick wrappers + matrix heatmaps) — read straight off
    // the canvas. This is the common, synchronous path.
    const canvas = this._resolveCanvas(canvasId)
    if (canvas) {
      try {
        this._triggerDownload(canvas.toDataURL("image/png", 1.0), canvasId)
      } catch (err) {
        // toDataURL throws SecurityError on a tainted canvas — surface feedback
        // instead of letting it escape unhandled (parity with the SVG path).
        this._announceFailure("Couldn't export this chart as a PNG.")
      }
      return
    }

    // SVG charts (Google GeoChart) — serialize + rasterize to PNG.
    const svg = this._resolveSvg(canvasId)
    if (svg) {
      this._downloadSvg(svg, canvasId)
      return
    }

    // Neither shape resolved. The server-side has-data gate means the button
    // only renders when data exists, so this is almost always an async chart
    // (GeoChart / remote Chartkick) that hasn't finished painting yet — tell the
    // user to retry rather than implying the chart has no data.
    this._announceFailure("The chart isn't ready yet — please try again in a moment.")
  }

  // Chartkick (`area_chart`, `column_chart`, `bar_chart`) renders a wrapper
  // <div id="..."> with a child <canvas>, while our hand-rolled matrix
  // heatmap binds the id directly to the <canvas>. Walk to the inner
  // canvas if needed so a single download button works for both shapes.
  _resolveCanvas(canvasId) {
    const node = document.getElementById(canvasId)
    if (!node) return null
    if (node instanceof HTMLCanvasElement) return node
    return node.querySelector("canvas")
  }

  // Google GeoChart cards bind the id to a container <div> that holds an
  // <svg>. The id may also point straight at an <svg>.
  _resolveSvg(canvasId) {
    const node = document.getElementById(canvasId)
    if (!node) return null
    if (node instanceof SVGSVGElement) return node
    return node.querySelector("svg")
  }

  // Serialize the <svg> and rasterize it to a PNG via an offscreen canvas.
  // The Image#onload step is asynchronous, so the success path and any
  // load/draw error both run from inside callbacks — everything is wrapped so
  // a failure surfaces feedback rather than throwing or silently dying.
  _downloadSvg(svg, canvasId) {
    let svgString
    try {
      svgString = new XMLSerializer().serializeToString(svg)
    } catch (err) {
      this._announceFailure("Couldn't read this chart for download.")
      return
    }

    const width  = svg.clientWidth  || parseInt(svg.getAttribute("width"), 10)  || 800
    const height = svg.clientHeight || parseInt(svg.getAttribute("height"), 10) || 600
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`

    try {
      const image = new Image()
      image.onload = () => {
        // The button may have been Turbo-swapped while the SVG was decoding;
        // don't trigger a surprise download on a controller the user has left.
        if (this._aborted || !this.element.isConnected) return
        try {
          const offscreen = document.createElement("canvas")
          offscreen.width  = width
          offscreen.height = height
          const ctx = offscreen.getContext("2d")
          // GeoChart svgs have a transparent background; paint white so the
          // exported PNG isn't see-through on dark viewers.
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, width, height)
          ctx.drawImage(image, 0, 0, width, height)
          this._triggerDownload(offscreen.toDataURL("image/png", 1.0), canvasId)
        } catch (err) {
          this._announceFailure("Couldn't export this chart as a PNG.")
        }
      }
      image.onerror = () => {
        this._announceFailure("Couldn't export this chart as a PNG.")
      }
      image.src = svgUrl
    } catch (err) {
      this._announceFailure("Couldn't export this chart as a PNG.")
    }
  }

  _triggerDownload(dataUrl, canvasId) {
    const link = document.createElement("a")
    link.download = this._resolvedFilename(canvasId)
    link.href = dataUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Surface an accessible, transient message next to the button. role="status"
  // + aria-live="polite" announce it to screen readers without stealing focus.
  // Built with textContent/DOM nodes (no innerHTML) to avoid any XSS surface,
  // mirroring geo_chart_controller#_renderEmptyState.
  _announceFailure(message) {
    // Don't announce on a controller whose button was Turbo-swapped out — async
    // failure callbacks (image.onerror / catch) can land here post-disconnect,
    // which would attach an orphaned node to a detached element.
    if (this._aborted || !this.element.isConnected) return

    // Reuse a single status node per button so repeated clicks don't stack.
    let status = this._statusNode
    if (!status || !status.isConnected) {
      status = document.createElement("span")
      // Visible to sighted users (small + muted) AND announced to screen readers
      // via role=status/aria-live. NOT `visually-hidden-focusable`: that hides
      // the node unless it gains focus, and a non-focusable <span> never does —
      // which would leave a failed click looking like a silent no-op (the exact
      // bug LRPO-5151 set out to fix).
      status.className = "chart-download__status small text-muted ms-2"
      status.setAttribute("role", "status")
      status.setAttribute("aria-live", "polite")
      this.element.insertAdjacentElement("afterend", status)
      this._statusNode = status
    }
    status.textContent = message

    if (this._statusTimer) clearTimeout(this._statusTimer)
    this._statusTimer = setTimeout(() => {
      if (this._statusNode && this._statusNode.isConnected) {
        this._statusNode.remove()
      }
      this._statusNode = null
    }, 5000)
  }

  _resolvedFilename(canvasId) {
    const name = this.filenameValue?.trim()
    if (name) return name.endsWith(".png") ? name : `${name}.png`
    return `${canvasId || "chart"}.png`
  }
}
